import { MouseEvent, ReactNode, memo, useEffect, useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import { useShallow } from 'zustand/react/shallow';
import { isTouchEnabled } from '../../Theme';
import storage from '../../hooks/useStorage/useStorage';
import { IMedia, mediaSorterAsc } from '../../lib/MediasLib';
import { saleItemIsKit } from '../../modules/catalogs/utils/saleItemType.utils';
import MediaRenderer from '../../modules/media/MediaRenderer';
import useOrderBook from '../../modules/order-book/hooks/useOrderBook';
import useSharedEntities from '../../modules/shared-entities/hooks/useSharedEntities';
import { IProductDetailsGridParams } from '../../services/modules/productDetails/productDetails.types';
import useGeneralSettingsStore from '../../stores/GeneralSettings/useGeneralSettingsStore';
import useOrderBookStore from '../../stores/OrderBook/orderBook.store';
import { OrderBookOrder } from '../../stores/OrderBook/orderBook.types';
import useProductDetailsStore from '../../stores/ProductDetails/productDetails.store';
import { ProductDetailsOriginType } from '../../stores/ProductDetails/productDetails.types';
import { prevent } from '../../utils/generics';
import { getMediaAspectRatio } from '../../utils/getMediaAspectRatio';
import useWidth from '../Hooks/useWidth';
import { useMediaApplicationContext } from '../MediaApplicationContext';
import ProductTagsRenderer from './ProductTagsRenderer';
import { CatalogItemRelations } from './components/ProductCard/types/productCard.types';
import ProductMediaEmptyState from './components/ProductMediaEmptyState/ProductMediaEmptyState';
import { ProductTag } from './hooks/useGetProductTagGroups';

const useStyles = makeStyles<{ showFullWidthTagsContainer: boolean }>()(
  (_, { showFullWidthTagsContainer }) => ({
    root: {
      position: 'relative',
      cursor: 'pointer',
    },
    tagsContainer: {
      position: 'absolute',
      width: showFullWidthTagsContainer ? 'calc(100% - 16px)' : 'fit-content',
      display: 'flex',
      gap: '8px',
      bottom: 0,
      margin: 8,
    },
  }),
);

interface ProductCardMediaProps {
  entityRelations: CatalogItemRelations;
  medias?: IMedia[];
  catalogId?: string;
  bottomTags: ProductTag[] | null;
  overlay?: ReactNode;
}

const ProductCardMedia = ({
  medias,
  catalogId,
  bottomTags,
  entityRelations,
  overlay,
}: ProductCardMediaProps) => {
  const productMedias = (medias ?? []).sort(mediaSorterAsc);
  const [selectedMedia, setSelectedMedia] = useState(productMedias[0]);
  const { disableProductDetails } = useSharedEntities({});
  const { mobileWidth } = useWidth();
  const selectedOrder = useOrderBookStore((state) => state.selectedOrder);
  const { allOrders } = useOrderBook();

  const mediaFormat = useGeneralSettingsStore((state) => state.generalSettings.mediaFormat);

  const { productGridParams, setProductDetailsModalState, handleOpenProductDetailsModal, setFrom } =
    useProductDetailsStore(
      useShallow((productDetailsState) => ({
        setFrom: productDetailsState.setFrom,
        productGridParams: productDetailsState.productGridParams,
        setProductDetailsModalState: productDetailsState.setProductDetailsModalState,
        handleOpenProductDetailsModal: productDetailsState.handleOpenProductDetailsModal,
      })),
    );

  const { classes } = useStyles({
    showFullWidthTagsContainer:
      bottomTags?.some((tag) => tag.type === 'colorClassification') ?? false,
  });

  useEffect(() => {
    if (isTouchEnabled) {
      return;
    }

    const mediaToPreload = productMedias?.[1];
    if (!mediaToPreload) {
      return;
    }

    if (mediaToPreload.type === 'VIDEO') {
      const video = document.createElement('video');
      video.src = mediaContext.getMediaFromSize(mediaToPreload.url, 'sm');
      video.preload = 'auto';
      video.load();
    } else {
      const img = new Image();
      img.src = mediaContext.getMediaFromSize(mediaToPreload.url, 'sm');
    }
  }, []);

  const mediaContext = useMediaApplicationContext();

  const applyProductDetailsFilters = () => {
    const productDetailsFilterSelectedByUser = storage.getProductDetailsFilter(
      storage.getSessionId() as string,
    );
    const isAllColorsFilterSelectedByUser = productDetailsFilterSelectedByUser?.applyAllColors;
    const isAllOrdersFilterSelectedByUser = productDetailsFilterSelectedByUser?.applyAllOrders;
    const isRelatedProductsFilterSelectedByUser =
      productDetailsFilterSelectedByUser?.applyRelatedProducts;
    const availabilityFilterSelectedByUser = productDetailsFilterSelectedByUser?.availabilityFilter;

    const orderIds = (() => {
      if (isAllOrdersFilterSelectedByUser && !mobileWidth) {
        return allOrders.map((order: OrderBookOrder) => order.id);
      }

      if (selectedOrder) {
        return [selectedOrder.id];
      }

      return [];
    })();

    if (saleItemIsKit(entityRelations)) {
      handleOpenProductDetailsModal(
        {
          ...productGridParams,
          orderIds,
          isFinishedOrder: false,
        },
        isRelatedProductsFilterSelectedByUser,
      );
      return;
    }

    const allProductGridParams: IProductDetailsGridParams = {
      ...productGridParams,
      availability: availabilityFilterSelectedByUser,
      productColorId: isAllColorsFilterSelectedByUser ? undefined : entityRelations.productColorId,
      orderIds,
      isFinishedOrder: false,
    };

    handleOpenProductDetailsModal(allProductGridParams, isRelatedProductsFilterSelectedByUser);
  };

  const openDetailsModal = () => {
    setFrom(catalogId ? { type: ProductDetailsOriginType.CATALOG, id: catalogId } : undefined);

    if (saleItemIsKit(entityRelations)) {
      setProductDetailsModalState({
        kitId: entityRelations.kitId,
        isOpen: true,
      });
      return;
    }

    setProductDetailsModalState({
      isOpen: true,
      productId: entityRelations.productId,
      productColorId: entityRelations.productColorId,
    });
  };

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    prevent(event);
    if (disableProductDetails) {
      return;
    }

    applyProductDetailsFilters();
    openDetailsModal();
  };

  const handleChangeSelectedMedia = (position: number) => {
    if (!productMedias[position]) {
      return;
    }

    setSelectedMedia(productMedias[position]);
  };

  const src =
    selectedMedia?.type === 'VIDEO'
      ? selectedMedia?.url
      : mediaContext.getMediaFromSize(selectedMedia?.url, 'sm');

  return (
    <div
      className={classes.root}
      onClick={handleClick}
      onMouseEnter={() => handleChangeSelectedMedia(1)}
      onMouseLeave={() => handleChangeSelectedMedia(0)}
    >
      {selectedMedia?.url ? (
        <MediaRenderer
          autoPlay
          src={src}
          type={selectedMedia.type}
          aspectRatio={getMediaAspectRatio(mediaFormat)}
        />
      ) : (
        <ProductMediaEmptyState aspectRatio={getMediaAspectRatio(mediaFormat)} />
      )}
      {bottomTags && (
        <div className={classes.tagsContainer}>
          <ProductTagsRenderer tags={bottomTags} variant="overlay" useSuffix={!mobileWidth} />
        </div>
      )}
      {overlay}
    </div>
  );
};

export default memo(ProductCardMedia);
